import React, { useState, useMemo } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine, LineChart, Line } from 'recharts';
import TeachingTooltip from '@/components/TeachingTooltip';
import { useTheme } from '@/context/ThemeContext';

const PerUnitBaseCalc = () => {
  const { dark } = useTheme();
  const [sBase, setSBase] = useState(100);
  const [vBase, setVBase] = useState(138);

  const zBase = useMemo(() => (vBase * vBase) / sBase, [vBase, sBase]);
  const iBase = useMemo(() => (sBase * 1000) / (Math.sqrt(3) * vBase), [sBase, vBase]);

  // Sensitivity: Z_base vs V_base, holding S_base constant
  const chartData = useMemo(() => {
    const points = [];
    const vMin = Math.max(1, vBase * 0.4);
    const vMax = vBase * 2.0;
    const step = (vMax - vMin) / 20;
    for (let v = vMin; v <= vMax + step / 2; v += step) {
      points.push({
        vKv: parseFloat(v.toFixed(1)),
        zOhm: parseFloat(((v * v) / sBase).toFixed(2)),
      });
    }
    return points;
  }, [vBase, sBase]);

  const axisColor = dark ? '#64748b' : '#94a3b8';
  const gridColor = dark ? '#1e293b' : '#f1f5f9';
  const tooltipBg = dark ? '#1e293b' : '#ffffff';
  const tooltipBorder = dark ? '#334155' : '#e2e8f0';

  return (
    <div data-testid="per-unit-base-calc" className="space-y-6">
      {/* Inputs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div className="space-y-2">
          <div className="flex items-center gap-1">
            <label className="text-sm font-medium text-slate-700 dark:text-slate-300">
              S<sub>base</sub> (MVA)
            </label>
            <TeachingTooltip
              showIcon
              whatThis="The three-phase apparent power base for your per-unit system. Most utilities use 100 MVA as their system base."
              tryThis="Change this from 100 MVA to 50 MVA and watch Z_base halve — a higher MVA base compresses all impedances."
              whyMatters="Choosing a consistent S_base across the whole network ensures transformers and lines can be directly compared."
              connectedTo="→ Updates: Z_base, I_base, and the sensitivity chart below."
            />
          </div>
          <div className="flex items-center gap-3">
            <input
              data-testid="sbase-input"
              type="range"
              min="1"
              max="1000"
              step="1"
              value={sBase}
              onChange={e => setSBase(Number(e.target.value))}
              className="flex-1 accent-teal-600"
            />
            <div className="flex items-center gap-1">
              <input
                type="number"
                value={sBase}
                min="1"
                max="1000"
                onChange={e => setSBase(Math.max(1, Math.min(1000, Number(e.target.value))))}
                className="w-20 text-sm border border-slate-300 dark:border-slate-600 rounded-md px-2 py-1 bg-white dark:bg-slate-800 text-slate-900 dark:text-white text-right focus:ring-1 focus:ring-teal-500 focus:border-teal-500 outline-none"
              />
              <span className="text-xs text-slate-500 w-9">MVA</span>
            </div>
          </div>
        </div>

        <div className="space-y-2">
          <div className="flex items-center gap-1">
            <label className="text-sm font-medium text-slate-700 dark:text-slate-300">
              V<sub>base</sub> (kV, line-to-line)
            </label>
            <TeachingTooltip
              showIcon
              whatThis="The line-to-line voltage base at the bus you're working on. Typical values: 765, 345, 138, 69, 13.8, 4.16 kV."
              tryThis="Double V_base from 138 kV to 276 kV and watch Z_base increase by 4× — the quadratic relationship is key."
              whyMatters="Voltage base changes across each transformer. Choose V_base to match the nominal voltage of the bus you're calculating."
              connectedTo="→ Updates: Z_base (quadratically), I_base (inversely), and both axes of the chart."
            />
          </div>
          <div className="flex items-center gap-3">
            <input
              data-testid="vbase-input"
              type="range"
              min="1"
              max="765"
              step="0.1"
              value={vBase}
              onChange={e => setVBase(Number(e.target.value))}
              className="flex-1 accent-teal-600"
            />
            <div className="flex items-center gap-1">
              <input
                type="number"
                value={vBase}
                min="1"
                max="765"
                step="0.1"
                onChange={e => setVBase(Math.max(1, Math.min(765, Number(e.target.value))))}
                className="w-20 text-sm border border-slate-300 dark:border-slate-600 rounded-md px-2 py-1 bg-white dark:bg-slate-800 text-slate-900 dark:text-white text-right focus:ring-1 focus:ring-teal-500 focus:border-teal-500 outline-none"
              />
              <span className="text-xs text-slate-500 w-6">kV</span>
            </div>
          </div>
        </div>
      </div>

      {/* Results */}
      <div className="grid grid-cols-2 gap-4">
        <div data-testid="zbase-result" className="bg-teal-50 dark:bg-teal-900/20 border border-teal-200 dark:border-teal-800 rounded-lg p-4 text-center">
          <p className="text-xs font-medium text-teal-700 dark:text-teal-400 uppercase tracking-widest mb-1">Z<sub>base</sub></p>
          <p className="text-2xl font-bold text-teal-900 dark:text-teal-100 font-mono">{zBase.toFixed(3)}</p>
          <p className="text-xs text-teal-600 dark:text-teal-500">Ohms</p>
        </div>
        <div data-testid="ibase-result" className="bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 rounded-lg p-4 text-center">
          <p className="text-xs font-medium text-slate-600 dark:text-slate-400 uppercase tracking-widest mb-1">I<sub>base</sub></p>
          <p className="text-2xl font-bold text-slate-900 dark:text-white font-mono">{iBase.toFixed(1)}</p>
          <p className="text-xs text-slate-500 dark:text-slate-500">Amperes</p>
        </div>
      </div>

      {/* Chart: Z_base sensitivity to V_base */}
      <div>
        <p className="text-xs font-medium text-slate-600 dark:text-slate-400 mb-2">
          Z<sub>base</sub> vs. V<sub>base</sub> — at S<sub>base</sub> = {sBase} MVA
          <span className="text-slate-400 dark:text-slate-500 ml-2">(shows quadratic V² relationship)</span>
        </p>
        <ResponsiveContainer width="100%" height={200}>
          <LineChart data={chartData} margin={{ top: 4, right: 16, bottom: 4, left: 8 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
            <XAxis
              dataKey="vKv"
              tick={{ fontSize: 10, fill: axisColor }}
              tickLine={false}
              axisLine={{ stroke: axisColor }}
              label={{ value: 'V_base (kV)', position: 'insideBottom', offset: -2, fill: axisColor, fontSize: 10 }}
            />
            <YAxis
              tick={{ fontSize: 10, fill: axisColor }}
              tickLine={false}
              axisLine={{ stroke: axisColor }}
              tickFormatter={v => `${v.toFixed(0)}Ω`}
            />
            <Tooltip
              contentStyle={{ background: tooltipBg, border: `1px solid ${tooltipBorder}`, borderRadius: '6px', fontSize: '11px' }}
              formatter={(v) => [`${v} Ω`, 'Z_base']}
              labelFormatter={(v) => `V_base = ${v} kV`}
            />
            <ReferenceLine x={vBase} stroke="#0d9488" strokeDasharray="4 2" />
            <Line
              type="monotone"
              dataKey="zOhm"
              stroke="#0d9488"
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 4, fill: '#0d9488' }}
            />
          </LineChart>
        </ResponsiveContainer>
        <p className="text-[10px] text-slate-400 dark:text-slate-600 mt-1 text-center">
          Dashed line = current V<sub>base</sub> = {vBase} kV → Z<sub>base</sub> = {zBase.toFixed(3)} Ω
        </p>
      </div>
    </div>
  );
};

export default PerUnitBaseCalc;
